"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  authClient,
  getSession,
  useLocalErrorDescription,
  type AuthErrorCode,
} from "@/lib/auth-client";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const t = useTranslations("Auth.SignIn");
  const { getLocalErrorDescription } = useLocalErrorDescription();

  getSession().then((session) => {
    if (session?.data) {
      router.push("/dashboard");
    }
  });

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authClient.signIn.email(
        {
          email,
          password,
        },
        {
          onError: (ctx) => {
            if (ctx.error.code) {
              toast.error(getLocalErrorDescription(ctx.error.code));
              return;
            }
            if (ctx.error.status === 403) {
              toast.error(t("emailNotVerified"));
            }
          },
          onSuccess: () => {
            getSession().then((session) => {
              if (session?.data?.user.role === "UNAPPROVED") {
                toast.error(t("accountUnapproved"));
                return;
              } else {
                toast.success(t("signInSuccess"));
                router.push("/dashboard");
              }
            });
          },
        }
      );
    } catch (error: unknown) {
      console.error("Sign in error:", error);
      if (error instanceof Error && "code" in error) {
        toast.error(getLocalErrorDescription(error.code as AuthErrorCode));
      } else {
        toast.error(t("signInError"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md p-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">{t("signIn")}</CardTitle>
            <CardDescription>{t("signin-description")}</CardDescription>
          </CardHeader>
          <form onSubmit={handleSignIn}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t("password")}</Label>
                  <Link
                    href="/auth/forgot-password"
                    className="text-sm text-primary hover:underline"
                  >
                    {t("forgotPassword")}
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Signing in..." : t("signIn")}
              </Button>
              <div className="text-center text-sm">
                {t("noAccount")}
                <Link
                  href="/auth/sign-up"
                  className="text-primary hover:underline"
                >
                  {t("signUp")}
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
